import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floating-label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock, Send, Navigation, Map } from "lucide-react";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { supabase } from "@/integrations/supabase/client";
import { alertToast } from "@/contexts/AlertToastContext";
import { motion } from "motion/react";
import { z } from "zod";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

// reCAPTCHA v3 site key - this is a PUBLIC key, safe to include in client code
const RECAPTCHA_SITE_KEY = "6Lc1x0wsAAAAACymcNFcmtNosfrWZvUvC84JUe4c";

// Extend Window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const Contact = () => {
  const { t } = useStaticTranslation("contact");
  const { t: seoT } = useStaticTranslation("seo");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  
  type ContactFormData = {
    name: string;
    email: string;
    phone?: string;
    message: string;
  };
  
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  const contactFormSchema = z.object({
    name: z.string()
      .trim()
      .min(1, { message: t("nameRequired", "שם הוא שדה חובה") })
      .max(100, { message: t("nameMax", "השם חייב להיות עד 100 תווים") }),
    email: z.string()
      .trim()
      .min(1, { message: t("emailRequired", "אימייל הוא שדה חובה") })
      .email({ message: t("emailInvalid", "כתובת אימייל לא תקינה") })
      .max(255, { message: t("emailMax", "האימייל חייב להיות עד 255 תווים") }),
    phone: z.string()
      .trim()
      .max(20, { message: t("phoneMax", "מספר הטלפון חייב להיות עד 20 תווים") })
      .regex(/^[\d\s\-+()]*$/, { message: t("phoneInvalid", "מספר טלפון לא תקין") })
      .optional()
      .or(z.literal("")),
    message: z.string()
      .trim()
      .min(1, { message: t("messageRequired", "הודעה היא שדה חובה") })
      .max(2000, { message: t("messageMax", "ההודעה חייבת להיות עד 2000 תווים") })
  });

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (document.querySelector(`script[src*="recaptcha"]`)) {
      setRecaptchaLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setRecaptchaLoaded(true);
      console.log("reCAPTCHA loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load reCAPTCHA");
    };
    document.head.appendChild(script);

    return () => {};
  }, []);

  const getRecaptchaToken = useCallback(async (): Promise<string | null> => {
    if (!recaptchaLoaded || !window.grecaptcha) {
      console.error("reCAPTCHA not loaded");
      return null;
    }

    try {
      await new Promise<void>((resolve) => {
        window.grecaptcha.ready(() => resolve());
      });
      
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: "contact_form"
      });
      return token;
    } catch (error) {
      console.error("Error getting reCAPTCHA token:", error);
      return null;
    }
  }, [recaptchaLoaded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = contactFormSchema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof ContactFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      alertToast.error(t("fixFields", "אנא תקן את השדות המסומנים"));
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await getRecaptchaToken();
      
      if (!recaptchaToken) {
        alertToast.error(t("securityFailed", "אימות אבטחה נכשל, אנא רענן את הדף ונסה שוב"));
        setIsSubmitting(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('submit-contact', {
        body: {
          name: result.data.name,
          email: result.data.email,
          phone: result.data.phone || null,
          message: result.data.message,
          recaptchaToken
        }
      });

      if (error) throw error;
      
      if (data?.error) {
        alertToast.error(data.error);
        setIsSubmitting(false);
        return;
      }

      // Send to webhook (non-blocking)
      try {
        await supabase.functions.invoke('send-to-webhook', {
          body: {
            from: 'contact',
            data: {
              name: result.data.name,
              email: result.data.email,
              phone: result.data.phone || null,
              message: result.data.message
            }
          }
        });
      } catch (webhookError) {
        console.error('Webhook error (non-blocking):', webhookError);
      }

      alertToast.success(t("messageSent", "הודעתך נשלחה בהצלחה!"));
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alertToast.error(t("sendError", "אירעה שגיאה בשליחת ההודעה. אנא נסה שוב."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t("address", "כתובת"),
      content: t("addressValue", "רחוב הרצל 58, באר שבע"),
      delay: 0.1,
      navigationLinks: {
        waze: "https://ul.waze.com/ul?venue_id=22806840.228002868.77916&overview=yes&utm_campaign=default&utm_source=waze_website&utm_medium=lm_share_location",
        googleMaps: "https://www.google.com/maps/place/%D7%90%D7%9C%D7%99%D7%A1+%7C+%D7%AA%D7%9B%D7%A9%D7%99%D7%98%D7%99%D7%9D+%D7%95%D7%A8%D7%A7%D7%95%D7%A8%D7%99%D7%9D/@31.241175,34.792080,17z"
      }
    },
    {
      icon: Phone,
      title: t("phoneLabel", "טלפון"),
      content: "079-5575576",
      isLtr: true,
      delay: 0.15
    },
    {
      icon: Mail,
      title: t("emailLabel", "אימייל"),
      content: "info@alicejewelry.net",
      delay: 0.2
    }
  ];

  const businessHours = [
    { day: t("sunday", "ראשון"), hours: "10:00 - 19:00" },
    { day: t("monday", "שני"), hours: "10:00 - 19:00" },
    { day: t("tuesday", "שלישי"), hours: "09:45 - 14:00" },
    { day: t("wednesday", "רביעי"), hours: "10:00 - 19:00" },
    { day: t("thursday", "חמישי"), hours: "10:00 - 19:00" },
    { day: t("fridayHolidays", "שישי וערבי חג"), hours: "09:45 - 14:00" },
    { day: t("saturdayHolidays", "שבתות וחגים"), hours: t("closed", "סגור") }
  ];

  return (
    <PageTransition>
      <SEO
        title={seoT("contactTitle", "חנות תכשיטים בבאר שבע, הרצל 58")}
        description={seoT("contactDescription", "טבעות אירוסין; תכשיטי ילדים; טבעות נישואין; חריטות ועיצוב אישי; טבעות זהב. בקרו אותנו ברחוב הרצל 58, באר שבע או התקשרו 079-5575576")}
        canonical="https://alice.jewelry/contact"
        breadcrumbs={[
          { name: 'דף הבית', url: '/' },
          { name: 'צור קשר', url: '/contact' },
        ]}
      />
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-background via-primary/[0.02] to-background" dir="rtl">
        <Header />
        
        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-20 px-4">
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
              <div className="absolute top-10 right-10 w-72 h-72 bg-primary/[0.03] rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 left-10 w-96 h-96 bg-primary/[0.02] rounded-full blur-3xl"></div>
            </div>
            
            <div className="container mx-auto max-w-6xl relative z-10">
              <motion.div 
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-sm text-primary/60 tracking-[0.3em] uppercase mb-4" style={{ fontWeight: 300 }}>
                  {t("heroSubtitle", "נשמח לשמוע ממך")}
                </p>
                <h1 className="text-4xl md:text-5xl mb-4 tracking-wide" style={{ fontWeight: 300 }}>
                  {t("heroTitle", "צור קשר")}
                </h1>
                <div className="flex items-center justify-center gap-2">
                  <div className="w-20 h-px bg-gradient-to-r from-transparent to-primary/40"></div>
                  <div className="w-1.5 h-1.5 bg-primary/60 rotate-45"></div>
                  <div className="w-20 h-px bg-gradient-to-l from-transparent to-primary/40"></div>
                </div>
              </motion.div>

              <div className="grid lg:grid-cols-2 gap-12 relative items-start min-h-[800px]">
                {/* Contact Form - Sticky */}
                <div className="order-2 lg:order-1 sticky top-24 self-start z-20 h-fit">
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <div className="bg-card/50 backdrop-blur-sm border border-border/30 rounded-2xl p-8">
                      <h2 className="text-2xl mb-6" style={{ fontWeight: 400 }}>{t("sendMessage", "שלח לנו הודעה")}</h2>
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-1">
                          <FloatingLabelInput 
                            id="name"
                            label={t("fullName", "שם מלא *")}
                            className={`text-right h-12 bg-background/50 border-border/50 focus:border-primary/50 ${errors.name ? 'border-destructive' : ''}`}
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            maxLength={100}
                          />
                          {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <FloatingLabelInput 
                              id="email"
                              type="email" 
                              label={t("email", "אימייל *")}
                              className={`text-right h-12 bg-background/50 border-border/50 focus:border-primary/50 ${errors.email ? 'border-destructive' : ''}`}
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              maxLength={255}
                            />
                            {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
                          </div>
                          <div className="space-y-1">
                            <FloatingLabelInput 
                              id="phone"
                              type="tel" 
                              label={t("phone", "טלפון")}
                              className={`text-right h-12 bg-background/50 border-border/50 focus:border-primary/50 ${errors.phone ? 'border-destructive' : ''}`}
                              value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              maxLength={20}
                            />
                            {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone}</p>}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Textarea 
                            placeholder={t("messagePlaceholder", "הודעה *")} 
                            className={`min-h-32 text-right bg-background/50 border-border/50 focus:border-primary/50 rounded-lg resize-none ${errors.message ? 'border-destructive' : ''}`}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            maxLength={2000}
                          />
                          {errors.message && <p className="text-sm text-destructive mt-1">{errors.message}</p>}
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full h-12 text-base gap-2 rounded-lg" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            t("sending", "שולח...")
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              {t("send", "שלח הודעה")}
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center leading-relaxed">
                          {t("privacyNote", "*אל דאגה, פרטים אלה נשמרים למטרת יצירת קשר בלבד בהתאם ל")}<a href="/privacy" className="text-primary hover:underline">{t("privacyPolicy", "מדיניות הפרטיות")}</a> {t("and", "ו")}<a href="/terms" className="text-primary hover:underline">{t("termsOfUse", "תנאי השימוש")}</a>
                        </p>
                      </form>
                    </div>
                  </motion.div>
                </div>

                {/* Contact Info */}
                <motion.div 
                  className="order-1 lg:order-2 space-y-6"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  {/* Contact Cards */}
                  <div className="space-y-4">
                    {contactInfo.map((info) => (
                      <motion.div
                        key={info.title}
                        className="flex items-center gap-4 p-5 bg-card/50 backdrop-blur-sm border border-border/30 rounded-xl hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: info.delay }}
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl flex items-center justify-center flex-shrink-0">
                          <info.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="text-right flex-1">
                          <h3 className="text-xs text-muted-foreground mb-1">{info.title}</h3>
                          <p
                            className="text-base text-foreground text-right"
                            style={{ fontWeight: 500 }}
                            dir={info.isLtr ? "ltr" : "rtl"}
                          >
                            {info.content}
                          </p>

                          {info.navigationLinks && (
                            <div className="flex gap-3 mt-3">
                              <a
                                href={info.navigationLinks.waze}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
                              >
                                <Navigation className="h-4 w-4" />
                                <span>Waze</span>
                              </a>
                              <a
                                href={info.navigationLinks.googleMaps}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
                              >
                                <Map className="h-4 w-4" />
                                <span>Google Maps</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Business Hours */}
                  <motion.div
                    className="flex items-start gap-4 p-5 bg-card/50 backdrop-blur-sm border border-border/30 rounded-xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.25 }}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>

                    <div className="flex-1 text-right">
                      <h3 className="text-xs text-muted-foreground mb-3">{t("businessHours", "שעות פעילות")}</h3>
                      <div className="space-y-3">
                        {businessHours.map((item, index) => (
                          <div
                            key={item.day}
                            className={`flex items-center justify-between py-2 ${index !== businessHours.length - 1 ? "border-b border-border/30" : ""}`}
                          >
                            <span className="text-sm text-foreground">{item.day}</span>
                            <span
                              className={`text-sm ${item.hours === t("closed", "סגור") ? "text-destructive" : "text-muted-foreground"}`}
                              dir={item.hours === t("closed", "סגור") ? "rtl" : "ltr"}
                            >
                              {item.hours}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Map Section */}
          <section className="pb-20 px-4">
            <div className="container mx-auto max-w-6xl">
              <motion.div 
                className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/30"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-background/10 to-transparent pointer-events-none z-10"></div>
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3411.261797287465!2d34.79208012489179!3d31.241175260883782!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x150266113eb89b75%3A0xa6850d035d15c6e8!2z15DXnNeZ16EgfCDXqteb16nXmdeY15nXnSDXldeo16fXldeo15nXnQ!5e0!3m2!1siw!2sil!4v1764110241227!5m2!1siw!2sil" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t("mapTitle", "מיקום Alice Jewelry")}
                  className="w-full"
                />
              </motion.div>
            </div>
          </section>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Contact;
